/**
 * Date Helper
 * Centralized date formatting functions for consistent date handling across the app
 * Supports locale-specific formatting for Israel (he-IL) and USA (en-US)
 */

import moment from 'moment';
import i18n from '../i18n/config';

/**
 * Get current locale from i18n
 */
const getCurrentLocale = (): string => {
  return i18n.language || 'en';
};

/**
 * Check if current locale is Hebrew (Israel)
 */
const isHebrewLocale = (): boolean => {
  return getCurrentLocale().startsWith('he');
};

/**
 * Format date and time based on locale
 * 🇮🇱 Israel (he-IL): DD/MM/YYYY HH:mm (24-hour)
 * 🇺🇸 USA (en-US): MM/DD/YYYY h:mm A (12-hour with AM/PM)
 * 
 * @param date - The date to format (string, Date, or moment object)
 * @returns Formatted date-time string or empty string if invalid
 */
export const getDateTime = (date: any): string => {
  if (!date) return '';
 
  const isHebrew = isHebrewLocale();
 
  const formats = {
    hebrew: 'DD/MM/YYYY HH:mm',
    english: 'MM/DD/YYYY h:mm A'
  };
  
  return moment(date).format(isHebrew ? formats.hebrew : formats.english);
};

/**
 * Format date based on locale
 * 🇮🇱 Israel (he-IL): DD/MM/YYYY
 * 🇺🇸 USA (en-US): MM/DD/YYYY
 * 
 * @param date - The date to format (string, Date, or moment object)
 * @returns Formatted date string or empty string if invalid
 */
export const getDate = (date: any): string => {
  if (!date) return '';
  
  const isHebrew = isHebrewLocale();
  
  const formats = {
    hebrew: 'DD/MM/YYYY',
    english: 'MM/DD/YYYY'
  };
  
  return moment(date).format(isHebrew ? formats.hebrew : formats.english);
};

/**
 * Format time based on locale
 * 🇮🇱 Israel (he-IL): HH:mm (24-hour)
 * 🇺🇸 USA (en-US): h:mm A (12-hour with AM/PM)
 * 
 * @param date - The date to format (string, Date, or moment object)
 * @returns Formatted time string or empty string if invalid
 */
export const getTime = (date: any): string => {
  if (!date) return '';
  
  const isHebrew = isHebrewLocale();
  
  if (isHebrew) {
    // 🇮🇱 Israel: HH:mm (24-hour)
    return moment(date).format('HH:mm');
  } else {
    // 🇺🇸 USA: h:mm A (12-hour with AM/PM)
    return moment(date).format('h:mm A');
  }
};

/**
 * Format date with custom format
 * @param date - The date to format (string, Date, or moment object)
 * @param format - Moment.js format string
 * @returns Formatted date string or empty string if invalid
 */
export const formatDate = (date: any, format: string): string => {
  if (!date) return '';
  return moment(date).format(format);
};

export const addDays = (date: string | Date | number, days: number): Date => {
  const result = new Date(date as any);
  result.setDate(result.getDate() + days);
  return result;
};
